import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, CopyPlus, History, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { ConceptCatalogDialog } from '../components/catalog/ConceptCatalogDialog';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, fmtMoney, fmtDate } from '../lib/format';

const FIELDS = {
  materials: [
    { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
    { key: 'unidad', label: 'Unidad', type: 'text' },
    { key: 'costo_unitario', label: 'Costo unitario', type: 'number' },
    { key: 'moneda', label: 'Moneda', type: 'moneda' },
    { key: 'proveedor', label: 'Proveedor', type: 'text' },
  ],
  labor: [
    { key: 'categoria', label: 'Categoría', type: 'text', required: true },
    { key: 'salario_base', label: 'Salario base', type: 'number' },
    { key: 'factor_salario_real', label: 'Factor salario real', type: 'number' },
    { key: 'rendimiento', label: 'Rendimiento', type: 'number' },
    { key: 'moneda', label: 'Moneda', type: 'moneda' },
  ],
  equipment: [
    { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
    { key: 'tipo', label: 'Tipo', type: 'text' },
    { key: 'costo_unitario', label: 'Costo unitario', type: 'number' },
    { key: 'moneda', label: 'Moneda', type: 'moneda' },
  ],
};

const COLUMNS = {
  materials: [['numero', 'Número'], ['descripcion', 'Descripción'], ['unidad', 'Unidad'], ['costo_unitario', 'Costo', 'money'], ['moneda', 'Moneda'], ['proveedor', 'Proveedor']],
  labor: [['numero', 'Número'], ['categoria', 'Categoría'], ['salario_base', 'Salario base', 'money'], ['factor_salario_real', 'Factor'], ['rendimiento', 'Rendimiento'], ['moneda', 'Moneda']],
  equipment: [['numero', 'Número'], ['descripcion', 'Descripción'], ['tipo', 'Tipo'], ['costo_unitario', 'Costo', 'money'], ['moneda', 'Moneda']],
  concepts: [['numero', 'Número'], ['descripcion', 'Descripción'], ['unidad', 'Unidad'], ['indirect_percentage', '% Ind.'], ['utility_percentage', '% Util.']],
};

const TAB_LABELS = { materials: 'Materiales', labor: 'Mano de Obra', equipment: 'Equipo', concepts: 'Conceptos' };

export default function CatalogoPage({ scope = 'prefab' }) {
  const queryClient = useQueryClient();
  const { canDelete } = useAuth();
  const [tab, setTab] = useState('materials');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [conceptDialogOpen, setConceptDialogOpen] = useState(false);
  const [conceptEditId, setConceptEditId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['catalog', tab, scope, search],
    queryFn: async () => (await api.get(`/catalog/${tab}`, { params: { scope, search: search || undefined, limit: 200 } })).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['catalog'] });
  const onError = (e) => toast.error(errMsg(e));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { ...form, scope };
      if (editId) return (await api.put(`/catalog/${tab}/${editId}`, payload)).data;
      return (await api.post(`/catalog/${tab}`, payload)).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editId ? 'Actualizado. Los cambios de precio se propagan a borradores.' : 'Registrado en catálogo');
      setDialogOpen(false);
    },
    onError,
  });

  const saveAsNewMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/catalog/${tab}/${id}/save-as-new`, {})).data,
    onSuccess: (item) => { invalidate(); toast.success(`Guardado como ${item.numero || item.codigo}`); },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/catalog/${tab}/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success('Eliminado del catálogo'); },
    onError,
  });

  const sendToPrefabMutation = useMutation({
    mutationFn: async (id) => (await api.post('/catalog/send-to-prefab', { catalog_type: tab, item_id: id })).data,
    onSuccess: (r) => { invalidate(); toast.success(r.mensaje || 'Enviado al catálogo Prefab'); },
    onError,
  });

  const showHistory = async (id) => {
    try {
      const { data: hist } = await api.get(`/catalog/${tab}/${id}/price-history`);
      setHistoryData(hist);
      setHistoryOpen(true);
    } catch (e) { onError(e); }
  };

  const openNew = () => {
    if (tab === 'concepts') { setConceptEditId(null); setConceptDialogOpen(true); return; }
    setForm({ moneda: 'MXN' }); setEditId(null); setDialogOpen(true);
  };
  const openEdit = (item) => {
    if (tab === 'concepts') { setConceptEditId(item.id); setConceptDialogOpen(true); return; }
    setForm(item); setEditId(item.id); setDialogOpen(true);
  };

  const fields = FIELDS[tab] || [];
  const columns = COLUMNS[tab] || [];
  const items = data?.items || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Catálogo {scope === 'pv' ? 'PV (Fotovoltaico)' : 'Prefab'}</h2>
          <p className="text-sm text-muted-foreground">Insumos reutilizables · cambios de precio se propagan a cotizaciones en borrador</p>
        </div>
        {tab !== 'concepts' ? (
          <Button onClick={openNew} data-testid="new-catalog-item-button">
            <Plus size={16} className="mr-1.5" /> Nuevo registro
          </Button>
        ) : (
          <Button onClick={openNew} data-testid="new-catalog-concept-button">
            <Plus size={16} className="mr-1.5" /> Nuevo concepto
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(''); }}>
        <TabsList data-testid="catalog-tabs">
          {Object.entries(TAB_LABELS).map(([k, v]) => (
            <TabsTrigger key={k} value={k} data-testid={`catalog-tab-${k}`}>{v}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por número o descripción..." className="pl-9" data-testid="catalog-search-input" />
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="catalog-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {columns.map(([key, label, type]) => (
                    <TableHead key={key} className={`th-label ${type === 'money' ? 'text-right' : ''}`}>{label}</TableHead>
                  ))}
                  <TableHead className="th-label w-14"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-muted-foreground py-8">Sin registros en este catálogo</TableCell></TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.id} className="hover:bg-background/40" data-testid={`catalog-row-${item.numero || item.id}`}>
                    {columns.map(([key, , type]) => (
                      <TableCell key={key} className={type === 'money' ? 'money' : key === 'numero' ? 'font-mono text-[13px]' : ''}>
                        {type === 'money' ? fmtMoney(item[key], item.moneda) : (item[key] ?? '—')}
                        {key === 'numero' && item.auto_registrado && <Badge variant="secondary" className="ml-2 text-[9px] px-1.5">auto</Badge>}
                      </TableCell>
                    ))}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`catalog-actions-${item.numero || item.id}`}>
                            <MoreHorizontal size={15} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)} data-testid="catalog-action-edit">
                            <Pencil size={14} className="mr-2" /> {tab === 'concepts' ? 'Editar detalle (APU)' : 'Editar'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => saveAsNewMutation.mutate(item.id)} data-testid="catalog-action-save-as-new">
                            <CopyPlus size={14} className="mr-2" /> Guardar como nuevo (-V2)
                          </DropdownMenuItem>
                          {tab !== 'concepts' && (
                            <DropdownMenuItem onClick={() => showHistory(item.id)} data-testid="catalog-action-history">
                              <History size={14} className="mr-2" /> Historial de precios
                            </DropdownMenuItem>
                          )}
                          {scope === 'pv' && (
                            <DropdownMenuItem onClick={() => sendToPrefabMutation.mutate(item.id)} data-testid="catalog-action-send-prefab">
                              <Send size={14} className="mr-2" /> Enviar a Catálogo Prefab
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => deleteMutation.mutate(item.id)} data-testid="catalog-action-delete">
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="catalog-item-dialog">
          <DialogHeader><DialogTitle>{editId ? `Editar ${form.numero || ''}` : 'Nuevo registro'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}{f.required ? ' *' : ''}</Label>
                {f.type === 'moneda' ? (
                  <Select value={form[f.key] || 'MXN'} onValueChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}>
                    <SelectTrigger data-testid={`catalog-field-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type}
                    step={f.type === 'number' ? '0.01' : undefined}
                    value={form[f.key] ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value }))}
                    data-testid={`catalog-field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="save-catalog-item-button">
              {editId ? 'Guardar cambios' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog editor completo de concepto (datos + APU embebido) */}
      <ConceptCatalogDialog
        open={conceptDialogOpen}
        onOpenChange={setConceptDialogOpen}
        itemId={conceptEditId}
        scope={scope}
      />

      {/* Dialog historial de precios */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md" data-testid="price-history-dialog">
          <DialogHeader><DialogTitle>Historial de precios</DialogTitle></DialogHeader>
          {historyData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Sin cambios de precio registrados</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Fecha</TableHead>
                  <TableHead className="th-label text-right">Anterior</TableHead>
                  <TableHead className="th-label text-right">Nuevo</TableHead>
                  <TableHead className="th-label">Por</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyData.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-[13px]">{fmtDate(h.created_at)}</TableCell>
                    <TableCell className="money">{fmtMoney(h.old_price)}</TableCell>
                    <TableCell className="money text-primary">{fmtMoney(h.new_price)}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{h.changed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
